/**
 * The shared bound on coaching note fields, and the normaliser that decides
 * what a draft actually stores.
 *
 * Both the Zod schemas in the curriculum router and the admin note editors
 * read these. They were briefly two hand-kept copies, which is the kind of
 * duplication that fails quietly in one direction only: raise the server bound
 * and the coach is still refused at the old number with no signal, lower it and
 * they meet an unreadable Zod rejection the client check exists to prevent.
 *
 * This module is imported from both `api/` and `components/`, so it stays free
 * of imports — anything it pulled in would be transpiled into the serverless
 * bundle as well.
 */

/**
 * Long enough for real guidance, bounded so a note is not an unbounded text
 * sink. Both note fields share the cap so the two editors behave alike.
 */
export const MAX_NOTE_LENGTH = 4000

/**
 * Blank and whitespace-only both mean "no note", so they store as null.
 *
 * The editor measures the result of this rather than the raw draft: the
 * trimmed string is what actually gets sent, so measuring the draft would
 * refuse a note the server would have accepted — a full-length note followed by
 * a stray newline reads as over the limit while the value on the wire is not.
 */
export function toStoredNote(draft: string): string | null {
  const trimmed = draft.trim()
  return trimmed === '' ? null : trimmed
}
